import { useCallback, useMemo } from "react";
import { useApolloClient } from "@apollo/client";

import { useAppDispatch, useAppSelector } from "../../../Store";


import { gqlActions } from "../gqlData_Reducer";
import Graph_QL, { DynamicDataObk, GqlHook_Returns, StateDataObject } from "..";
import { noop } from "lodash";
import { filterByConnection } from "../filterByConnection";

import { GET_ARCHIVED_REPORTS, ArchivedReportResponse } from "./GQLCalls/getArchivedReportsCall";
import { ReportItem } from "./reportSlice";
import { BasicQueryInfoInput } from "../FeedDataCalls/GQLCalls/getFeedComponentsCall";
import { ReportData } from "../../../Definitions";
import { UPDATE_REPORT, UpdateReportResponse } from "./GQLCalls/updateReportCall";
import { MARK_REPORT_AS_DELETED, MarkReportAsDeletedResponse } from "./GQLCalls/deleteReportCall";
import { CREATE_REPORT, CreateReportResponse } from "./GQLCalls/createReportCall";

type FetchReportDataDirective = {ifFetchData: boolean} 

interface ReturnChanges extends Omit<GqlHook_Returns<ReportItem, BasicQueryInfoInput & FetchReportDataDirective>, "maintainEntries"> {
  maintainEntries: (updatedReport?: Partial<DynamicDataObk<ReportData>>, deletedReport?: Partial<DynamicDataObk<number[]>>, createReport?: Partial<DynamicDataObk<Omit<ReportData, "reportId">>>) => Promise<number[]>
}

export const useReport = (connection: Graph_QL.GQLCons): ReturnChanges  => {
  const dispatch = useAppDispatch();
  const client = useApolloClient();
  // const [, setCursorId] = useState(-1);

  const reportState = useAppSelector(({graphQlData:{domain}}) => domain.report);
  const {fullState} = useMemo(() => {
    const state = filterByConnection(reportState, Graph_QL.ToArr(connection)) as Record<string, ReportItem>
    return Object.entries(state).reduce((accumulator, [key, data]) => {
      if (data.data) {
        const value = {...data.data, domain:key}; 
        accumulator.data = accumulator.data.concat(value)
      }
      return accumulator
    },{fullState: state, data: []} as StateDataObject<ReportItem>)
  },[connection, reportState]);

  const getReports = useCallback(async (inputWithDirective: BasicQueryInfoInput & FetchReportDataDirective, specific?: Graph_QL.GQLCons) => {
    const specificConnection = specific ? specific : connection;
    const {ifFetchData,...input} = inputWithDirective;
    try {
      client.query<ArchivedReportResponse>({query: GET_ARCHIVED_REPORTS, variables: {...specificConnection.connection, input, ifFetchData }})
        .then((e) => {
          if (e && e.data) {
            dispatch({type: String(gqlActions.domain.report.maintained), payload: {con: specificConnection.connection, data: e.data.report.archived}});
          }
        }).catch((error) => {console.log(error);})
    } catch (error) {
        console.log(error);
      }
    },[client, connection, dispatch])

    const maintainEntries = async (updatedReport?: Partial<DynamicDataObk<ReportData>>, deletedReport?: Partial<DynamicDataObk<number[]>>, createReport?: Partial<DynamicDataObk<Omit<ReportData, "reportId">>>) => {
      const affectedIds: number[] = []
      try {
        if (updatedReport?.data) {
          const updateResult = await client.mutate<UpdateReportResponse>({mutation: UPDATE_REPORT, variables: {...connection.connection, input: [updatedReport.data], ifFetchData: false}})
          if (updateResult && updateResult.data && updateResult.data.report.update.success) {
            const updatedIds = updateResult.data.report.update.updatedValues?.map((report) => report.reportId!);
            if (updatedIds) {
              affectedIds.push(...updatedIds);
            }
            dispatch({type: String(gqlActions.domain.report.maintained), payload: {con: connection.connection, data: updateResult.data.report.update.updatedValues}})
          }
        }

        if (deletedReport?.data?.length) {
          const deleteResult = await client.mutate<MarkReportAsDeletedResponse>({mutation: MARK_REPORT_AS_DELETED, variables: {...connection.connection, ids: deletedReport.data}})
          if (deleteResult && deleteResult.data && deleteResult.data.report.markAsDeleted.success) {
            const deletedIds = deleteResult.data.report.markAsDeleted.updatedValues?.map((report) => report.reportId!);
            if (deletedIds) {
              affectedIds.push(...deletedIds);
            }
            dispatch({type: String(gqlActions.domain.report.maintained), payload: {con: connection.connection, data: deleteResult.data.report.markAsDeleted.updatedValues}})
          }
        }

        if (createReport?.data) {
          const createResult = await client.mutate<CreateReportResponse>({mutation: CREATE_REPORT, variables: {...connection.connection, ...createReport.data, ifFetchData: true}})
          if (createResult && createResult.data && createResult.data.report.create.success) {
            const createdIds = createResult.data.report.create.updatedValues?.map((report) => report.reportId!);
            if (createdIds) {
              affectedIds.push(...createdIds);
            }
            dispatch({type: String(gqlActions.domain.report.maintained), payload: {con: connection.connection, data: createResult.data.report.create.updatedValues}})
          }
        }
        return affectedIds;
      } catch (error) {
        console.log(error);
        return [];
      }
    }

  return {call: getReports, fetchMore: noop, deleteEntries: noop, data: fullState, maintainEntries}
} 

